import React, { useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { MdUpload, MdVisibility } from "react-icons/md";
import Button from "../../atoms/Button";
import Modal from "../../molecules/Modal";
import NoData from "../../atoms/NoData";
import useModal from "../../../hooks/useModal";
import ModalSubirComprobante from "../../molecules/DF/contratos/ModalSubirComprobante";
import { classifyPayment, calculatePaymentStats } from "../../../helpers/paymentHelpers";
import { sortPaymentsByMonthAscending } from "../../../helpers/globalPaymentHelpers";
import { toast } from "react-toastify";
import { GET_FILE } from "../../../api/apiFiles";
import { useParams } from "react-router-dom";

const PagosExpandedTable = ({ pagos, contractId, refetch }) => {
	const [documento, setDocumento] = useState();
	const { open, close, isOpen } = useModal();
	const [selectedPago, setSelectedPago] = useState(null);
	const { open: openUploadModal, close: closeUploadModal, isOpen: isUploadModalOpen } = useModal();
    const { id } = useParams();

	const handleOpenDocument = async (s3Key) => {
		try {
			const response = await GET_FILE(id, { s3Key });
			setDocumento(response);
			open();
		} catch (error) {
			console.error(error);
			toast.error("Error al obtener el archivo");
		}
	};

	// Ordenar pagos por mes ascendente (enero primero, diciembre último)
	const sortedPagos = useMemo(() => {
		return sortPaymentsByMonthAscending(pagos);
	}, [pagos]);

	// Calcular stats basadas en pagos ordenados
	const stats = useMemo(() => {
		return calculatePaymentStats(sortedPagos);
	}, [sortedPagos]);

	const formatCurrency = (value) => {
		return new Intl.NumberFormat("es-MX", {
			style: "currency",
			currency: "MXN",
		}).format(value);
	};

	const formatDate = (date) => {
		return new Date(date).toLocaleDateString("es-MX", {
			weekday: "short",
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	const getMonthName = (date) => {
		return new Date(date).toLocaleDateString("es-MX", {
			month: "long",
			year: "numeric",
		});
	};

	/**
	 * Retorna badge con la clasificación del pago
	 * PAGADO (verde) | DEUDA (rojo) | CORRIENTE (amarillo) | FUTURO (azul)
	 */
	const getClassificationBadge = (pago) => {
		const classification = classifyPayment(pago);

		const badgeStyles = {
			pagado: {
				bg: "bg-green-100",
				text: "text-green-800",
				label: "✓ Pagado",
			},
			deuda: {
				bg: "bg-red-100",
				text: "text-red-800",
				label: "⏰ Deuda",
			},
			corriente: {
				bg: "bg-yellow-100",
				text: "text-yellow-800",
				label: "📅 Corriente",
			},
			futuro: {
				bg: "bg-blue-100",
				text: "text-blue-800",
				label: "📆 Futuro",
			},
		};

		const style = badgeStyles[classification] || badgeStyles.futuro;

		return (
			<span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${style.bg} ${style.text}`}>
				{style.label}
			</span>
		);
	};

	const handleOpenUploadModal = (pago) => {
		setSelectedPago(pago);
		openUploadModal();
	};

	const handleCloseUploadModal = () => {
		setSelectedPago(null);
		closeUploadModal();
	};

	const columns = [
		{
			name: "Mes",
			cell: (row) => (
				<div className="flex flex-col">
					<p className="font-semibold">{getMonthName(row.date)}</p>
					<p className="text-xs text-gray-500">{formatDate(row.date)}</p>
				</div>
			),
		},
		{
			name: "Monto",
			selector: (row) => formatCurrency(row.payment),
		},
		{
			name: "Clasificación",
			cell: (row) => getClassificationBadge(row),
		},
		{
			name: "Archivo",
			cell: (row) => (
				<div className="flex items-center gap-2">
					{row.file ? (
						<Button
							text={<MdVisibility size={18} />}
							bgColor="blue"
							width="w-10"
              				onClick={() => handleOpenDocument(row?.file?.s3Key)}
							title="Ver comprobante"
						/>
					) : (
						<span className="text-xs text-gray-400">Sin archivo</span>
					)}
				</div>
			),
		},
		{
			name: "Acciones",
			cell: (row) => (
				<div className="flex gap-2">
					<Button
						text={<MdUpload size={18} />}
						bgColor="blue"
						width="w-10"
						onClick={() => handleOpenUploadModal(row)}
						title={row.status === "pagado" ? "Cambiar comprobante" : "Subir comprobante"}
					/>
				</div>
			),
		},
	];

	const columnsResponsive = [
		{
			name: "Detalles del Pago",
			cell: (row) => (
				<div className="flex flex-col space-y-3 py-2 w-full">
					<div className="flex justify-between">
						<span className="font-semibold text-sm">Mes:</span>
						<span className="text-sm">{getMonthName(row.date)}</span>
					</div>
					<div className="flex justify-between">
						<span className="font-semibold text-sm">Fecha:</span>
						<span className="text-xs text-gray-600">{formatDate(row.date)}</span>
					</div>
					<div className="flex justify-between">
						<span className="font-semibold text-sm">Monto:</span>
						<span className="text-sm font-bold">{formatCurrency(row.payment)}</span>
					</div>
					<div className="flex justify-between">
						<span className="font-semibold text-sm">Clasificación:</span>
						<span>{getClassificationBadge(row)}</span>
					</div>
					<div className="flex justify-between items-center pt-2 border-t">
						<span className="font-semibold text-sm">Archivo:</span>
						<div className="flex gap-2">
							{row.file ? (
								<Button
									text={<MdVisibility size={18} />}
									bgColor="blue"
									width="w-10"
									onClick={() => {
										console.log("Ver archivo", row.file);
									}}
								/>
							) : (
								<span className="text-xs text-gray-400">Sin archivo</span>
							)}
						</div>
					</div>
					<div className="flex justify-end gap-2 pt-2 border-t">
						<Button
							text={<div className="flex items-center gap-2"><MdUpload size={16} /> Subir</div>}
							bgColor="blue"
							onClick={() => handleOpenUploadModal(row)}
						/>
					</div>
				</div>
			),
		},
	];

	if (!sortedPagos || sortedPagos.length === 0) {
		return (
			<div className="p-4 bg-gray-50">
				<NoData />
			</div>
		);
	}

	return (
		<>
			<div className="p-4 bg-gray-50 w-full">
				<div className="mb-4">
					<h3 className="text-lg font-semibold text-gray-800 mb-2">
						Desglose de Pagos - Contrato #{contractId}
					</h3>
					<div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
						<div className="bg-green-50 border border-green-200 rounded p-3">
							<p className="text-xs text-gray-600 font-semibold">✓ Pagados</p>
							<p className="text-lg font-bold text-green-700">{stats.pagado.count}</p>
							<p className="text-xs text-gray-500">{formatCurrency(stats.pagado.total)}</p>
						</div>
						<div className={`rounded p-3 border ${stats.deuda.count > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-100 border-gray-300'}`}>
							<p className="text-xs text-gray-600 font-semibold">⏰ Deuda</p>
							<p className={`text-lg font-bold ${stats.deuda.count > 0 ? 'text-red-700' : 'text-gray-600'}`}>
								{stats.deuda.count}
							</p>
							<p className="text-xs text-gray-500">{formatCurrency(stats.deuda.total)}</p>
						</div>

						{/* Corriente */}
						<div className={`rounded p-3 border ${stats.corriente.count > 0 ? 'bg-yellow-50 border-yellow-200' : 'bg-gray-100 border-gray-300'}`}>
							<p className="text-xs text-gray-600 font-semibold">📅 Corriente</p>
							<p className={`text-lg font-bold ${stats.corriente.count > 0 ? 'text-yellow-700' : 'text-gray-600'}`}>
								{stats.corriente.count}
							</p>
							<p className="text-xs text-gray-500">{formatCurrency(stats.corriente.total)}</p>
						</div>
						<div className="bg-blue-50 border border-blue-200 rounded p-3">
							<p className="text-xs text-gray-600 font-semibold">📆 Futuro</p>
							<p className="text-lg font-bold text-blue-700">{stats.futuro.count}</p>
							<p className="text-xs text-gray-500">{formatCurrency(stats.futuro.total)}</p>
						</div>
					</div>
					{stats.hayDeuda && (
						<div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
							<p className="text-sm font-bold text-red-800 mb-1">⚠️ Información Importante</p>
							<p className="text-sm text-red-700 mb-2">
								<strong>Para ponerse al corriente debe pagar: </strong>
								{formatCurrency(stats.pagarAhoraPara)} (deuda)
							</p>
							<p className="text-sm text-red-700">
								<strong>Pendiente diferente (futuro): </strong>
								{formatCurrency(stats.pendienteLuego)}
							</p>
						</div>
					)}

					{!stats.hayDeuda && stats.corriente.count > 0 && (
						<div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
							<p className="text-sm font-bold text-yellow-800 mb-1">📅 Pagos Este Mes</p>
							<p className="text-sm text-yellow-700">
								Debe pagar {formatCurrency(stats.corriente.total)} este mes
							</p>
						</div>
					)}

					{!stats.hayDeuda && stats.corriente.count === 0 && (
						<div className="bg-green-50 border border-green-200 rounded p-3 mb-4">
							<p className="text-sm font-bold text-green-800">✓ Al Corriente</p>
							<p className="text-sm text-green-700">
								No tiene pagos vencidos. Próximos pagos: {formatCurrency(stats.pendienteLuego)}
							</p>
						</div>
					)}
				</div>

				{/* Tabla Desktop */}
				<div className="hidden md:block">
					<DataTable
						data={sortedPagos}
						columns={columns}
						noDataComponent={<NoData />}
						highlightOnHover
						striped
					/>
				</div>

				{/* Tabla Mobile */}
				<div className="block md:hidden">
					<DataTable
						data={sortedPagos}
						columns={columnsResponsive}
						noDataComponent={<NoData />}
						responsive
					/>
				</div>
			</div>

			{isUploadModalOpen && selectedPago && (
				<Modal 
					onClose={handleCloseUploadModal} 
					title="Subir Comprobante de Pago"
					width="w-full sm:w-[75%]"
				>
					<ModalSubirComprobante
						pago={selectedPago}
						onClose={handleCloseUploadModal}
						refetch={refetch}
					/>
				</Modal>
			)}
			{isOpen && (
                <Modal title="Previsualización del Documento" onClose={close}>
                    <div className="w-full h-[80vh] flex justify-center items-center">
						<iframe
							src={documento}
                          	className="h-full w-full"
							title="Documento Previsualizado"
						/>
                    </div>
                </Modal>
            )}
		</>
	);
};

export default PagosExpandedTable;